import React from 'react';
import { isMobile } from 'react-device-detect';
import styled, { keyframes } from 'styled-components';

const ScenariosSkeleton = ({ showTagsSkeleton = false }) => {
  return (
    <div>
      {showTagsSkeleton && (
        <TopSection>
          {isMobile ? (
            <>
              <TogglerRow>
                <SkeletonToggler />
              </TogglerRow>
              <TagsRow>
                <SkeletonTag style={{ width: '45px' }} />
                <SkeletonTag style={{ width: '85px' }} />
                <SkeletonTag style={{ width: '65px' }} />
                <SkeletonTag style={{ width: '120px' }} />
              </TagsRow>
              <TagsRow>
                <SkeletonTag style={{ width: '75px' }} />
                <SkeletonTag style={{ width: '115px' }} />
                <SkeletonTag style={{ width: '105px' }} />
              </TagsRow>
            </>
          ) : (
            <TagsSection>
              <SkeletonTag style={{ width: '45px' }} />
              <SkeletonTag style={{ width: '70px' }} />
              <SkeletonTag style={{ width: '85px' }} />
              <SkeletonTag style={{ width: '55px' }} />
              <SkeletonTag style={{ width: '75px' }} />
              <SkeletonTag style={{ width: '90px' }} />
            </TagsSection>
          )}
        </TopSection>
      )}
      <Root>
        {[...Array(8)].map((_, index) => (
          <Card key={index}>
            <ImageSection>
              <SkeletonImage />
            </ImageSection>
            <ContentSection>
              <SkeletonTitle />
              <TextContainer>
                <SkeletonText />
                <SkeletonText style={{ width: '85%' }} />
                <SkeletonText style={{ width: '90%' }} />
                <SkeletonText style={{ width: '70%' }} />
              </TextContainer>
              <BottomSection>
                <SkeletonPlay />
              </BottomSection>
            </ContentSection>
          </Card>
        ))}
      </Root>
    </div>
  );
};

export default ScenariosSkeleton;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const Root = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 1330px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    padding: 0;
    gap: 24px;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  background: #1D1D1D;
  width: 100%;
  min-height: 400px;
`;

const ImageSection = styled.div`
  width: 100%;
  height: 200px;
  border-radius: 12px 12px 0 0;
  overflow: hidden;
`;

const SkeletonImage = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.00) 0%, rgba(169, 169, 169, 0.50) 52.14%, rgba(169, 169, 169, 0.00) 100%), #696969;
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;
`;

const ContentSection = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #1D1D1D;
  flex: 1;

  @media (max-width: 700px) {
    padding: 16px;
    gap: 12px;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const SkeletonTitle = styled.div`
  width: 60%;
  height: 28px;
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.15) 0%, rgba(169, 169, 169, 0.45) 52.14%, rgba(169, 169, 169, 0.15) 100%), rgba(169, 169, 169, 0.2);
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;
  margin-bottom: 8px;
`;

const SkeletonText = styled.div`
  width: 100%;
  height: 18px;
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.15) 0%, rgba(169, 169, 169, 0.45) 52.14%, rgba(169, 169, 169, 0.15) 100%), rgba(169, 169, 169, 0.2);
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;
`;

const BottomSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
`;

const SkeletonPlay = styled.div`
  width: 86px;
  height: 50px;
  border-radius: 100px;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.15) 0%, rgba(169, 169, 169, 0.45) 52.14%, rgba(169, 169, 169, 0.15) 100%), rgba(169, 169, 169, 0.2);
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;
  margin-left: auto;
`;

const TopSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0.75rem 0 0.5rem 0;
  margin-bottom: 16px;

  @media (max-width: 700px) {
    margin-bottom: 0px;
    flex-direction: column;
    align-items: flex-start;
    gap: 0px;
  }
`;

const TogglerRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const TagsRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  margin-bottom: 8px;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const TagsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: none;
  -ms-overflow-style: none;
  flex: 1;
  padding-bottom: 4px;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SkeletonToggler = styled.div`
  width: 140px;
  height: 20px;
  border-radius: 100px;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.15) 0%, rgba(169, 169, 169, 0.45) 52.14%, rgba(169, 169, 169, 0.15) 100%), rgba(169, 169, 169, 0.2);
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;
  flex-shrink: 0;
`;

const SkeletonTag = styled.div`
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.15) 0%, rgba(169, 169, 169, 0.45) 52.14%, rgba(169, 169, 169, 0.15) 100%), #1D1D1D;
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;
  flex-shrink: 0;
`; 